import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json, searchParams } from '../api/client'
import type { AdminUser, AdminWorkspace, AdminWorkspaceDetail, Analytics, AuditEntry, Job, JobTypeInfo, Overview, PlanName, PriceView, ProviderTest, Setting, WebhookSetup, WorkerInfo } from '../api/types'

const adminKey = ['admin'] as const

export function useOverview() {
  return useQuery({ queryKey: [...adminKey, 'overview'], queryFn: () => api<Overview>('/admin/overview'), refetchInterval: 5_000 })
}

export function useSettings() {
  return useQuery({ queryKey: [...adminKey, 'settings'], queryFn: () => api<Setting[]>('/admin/settings') })
}

export function useSaveSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api<Setting>(`/admin/settings/${key}`, { method: 'PUT', body: json({ value }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useClearSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => api<Setting>(`/admin/settings/${key}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useTestProvider() {
  return useMutation({
    mutationFn: (provider: string) => api<ProviderTest>(`/admin/settings/test/${provider}`, { method: 'POST' }),
  })
}

/** Point Cloudflare Stream's video webhook at this deployment and save its secret. */
export function useConnectStreamWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<WebhookSetup>('/admin/live/webhook', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useJobTypes() {
  return useQuery({ queryKey: [...adminKey, 'job-types'], queryFn: () => api<JobTypeInfo[]>('/admin/job-types'), staleTime: Infinity })
}

/** Empty fields match everything. */
export interface JobFilter {
  status?: string
  job_type?: string
  workspace_id?: string
}

export function useJobs(filter: JobFilter) {
  return useQuery({
    queryKey: [...adminKey, 'jobs', filter],
    queryFn: () => api<Job[]>(`/admin/jobs${searchParams({ ...filter, limit: 50 })}`),
    refetchInterval: 2_000,
  })
}

/** Stop a queued or running job; it fails as cancelled and its credits are released. */
export function useCancelJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<Job>(`/admin/jobs/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useRetryFailed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (filter: { job_type?: string; workspace_id?: string }) =>
      api<{ retried: number }>('/admin/jobs/retry-failed', { method: 'POST', body: json(filter) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useEnqueueJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { job_type: string; payload: unknown }) => api<Job>('/admin/jobs', { method: 'POST', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useRetryJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<Job>(`/admin/jobs/${id}/retry`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKey }),
  })
}

export function useWorkers() {
  return useQuery({ queryKey: [...adminKey, 'workers'], queryFn: () => api<WorkerInfo[]>('/admin/workers'), refetchInterval: 5_000 })
}

export function useAdminWorkspaces(q: string) {
  return useQuery({ queryKey: [...adminKey, 'workspaces', q], queryFn: () => api<AdminWorkspace[]>(`/admin/workspaces${searchParams({ q })}`) })
}

export function useGrantCredits() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, ...input }: { workspaceId: string; credits: number; note: string }) =>
      api<AdminWorkspace>(`/admin/workspaces/${workspaceId}/credits`, { method: 'POST', body: json(input) }),
    // The admin's own badge may be for this workspace too.
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export function useSetPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, plan }: { workspaceId: string; plan: PlanName }) =>
      api<AdminWorkspace>(`/admin/workspaces/${workspaceId}/plan`, { method: 'PUT', body: json({ plan }) }),
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

/** What people did that is worth knowing later, newest first. */
export function useAudit(filters: { workspaceId?: string; action?: string }) {
  return useQuery({
    queryKey: [...adminKey, 'audit', filters.workspaceId ?? '', filters.action ?? ''],
    queryFn: () => api<AuditEntry[]>(`/admin/audit${searchParams({ workspace_id: filters.workspaceId, action: filters.action })}`),
  })
}

/** Stop a workspace making new work, or start it again. */
export function useSetWorkspaceStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, suspended, reason }: { workspaceId: string; suspended: boolean; reason?: string }) =>
      api<AdminWorkspace>(`/admin/workspaces/${workspaceId}/status`, { method: 'PUT', body: json({ suspended, reason }) }),
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

/** How many shows a workspace may have live at once (null: the default of one). */
export function useSetLiveLimit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, max }: { workspaceId: string; max: number | null }) =>
      api<void>(`/admin/workspaces/${workspaceId}/live-shows`, { method: 'PUT', body: json({ max_live_shows: max }) }),
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export function usePrices() {
  return useQuery({ queryKey: [...adminKey, 'prices'], queryFn: () => api<PriceView[]>('/admin/prices') })
}

export function useSetPrice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ task, unit, micro_credits }: { task: string; unit: string; micro_credits: number }) =>
      api<PriceView[]>(`/admin/prices/${task}/${unit}`, { method: 'PUT', body: json({ micro_credits }) }),
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export function useResetPrice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ task, unit }: { task: string; unit: string }) => api<PriceView[]>(`/admin/prices/${task}/${unit}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export function useAnalytics(days: number, workspaceId?: string) {
  return useQuery({
    queryKey: [...adminKey, 'analytics', days, workspaceId ?? ''],
    queryFn: () => api<Analytics>(`/admin/analytics${searchParams({ days, workspace_id: workspaceId })}`),
    refetchInterval: 60_000,
  })
}

export function useAdminUsers(q: string) {
  return useQuery({ queryKey: [...adminKey, 'users', q], queryFn: () => api<AdminUser[]>(`/admin/users${searchParams({ q })}`) })
}

export function useAdminWorkspace(id: string) {
  return useQuery({ queryKey: [...adminKey, 'workspace', id], queryFn: () => api<AdminWorkspaceDetail>(`/admin/workspaces/${id}`), enabled: !!id })
}
